import React from "react";

import { GanttChartConfigProps, IGanttTimestamp } from "components/ComponentTypeTypes";
import ColorPicker from "components/basic/color-picker/ColorPicker";
import { IProductTemplate } from "components/dashboard/product-template-table/utils/interfaces";
import { Button } from "primereact/button";
import { Column, ColumnBodyOptions } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { T } from "services/lang/LanguageService";
import { SymbolType } from "types/types";
import { symbols } from "utils/constants";
import { v4 as uuidv4 } from "uuid";
import { AttributeType } from "@energy-advice/easas-api";
import { getHexRandomColor } from "../../utils";

type GanttTimestampsProps = GanttChartConfigProps & {
  data: IProductTemplate | undefined;
};

const GanttTimestamps: React.FC<GanttTimestampsProps> = ({
  data,
  currentConfig,
  setCurrentConfig,
}) => {
  const handleTimestampChange = (
    body: IGanttTimestamp,
    e: React.ChangeEvent<HTMLInputElement> | DropdownChangeEvent
  ) => {
    setCurrentConfig(prev => ({
      ...prev,
      ganttTimestamps: prev.ganttTimestamps.map(ob =>
        ob.id === body.id ? { ...ob, [e.target.name]: e.target.value } : ob
      ),
    }));
  };

  const renderTitleInput = (body: IGanttTimestamp, options: ColumnBodyOptions) => (
    <InputText
      value={body.title ?? ""}
      type="text"
      className="p-inputtext-sm w-14rem"
      placeholder={options.field}
      name="title"
      onChange={e => handleTimestampChange(body, e)}
    />
  );

  const renderDropdown = (body: IGanttTimestamp) => (
    <Dropdown
      value={body.dateColumn ?? ""}
      options={data?.data?.attributes.filter(attr => attr.type !== AttributeType.DATETIME)}
      optionLabel="displayName"
      optionValue="referenceName"
      name="dateColumn"
      className="p-inputtext-sm w-14rem"
      onChange={e => handleTimestampChange(body, e)}
    />
  );

  const renderIconDropdown = (body: IGanttTimestamp) => {
    const itemTemplate = (option: SymbolType) => <span className="text-2xl">{option.char}</span>;
    return (
      <Dropdown
        value={body.icon ?? ""}
        options={symbols}
        optionLabel="char"
        optionValue="plotlySymbol"
        itemTemplate={itemTemplate}
        name="icon"
        className="p-inputtext-sm w-6rem"
        onChange={e => handleTimestampChange(body, e)}
      />
    );
  };

  const renderColorPicker = (body: IGanttTimestamp) => (
    <ColorPicker
      name="color"
      value={body.color}
      className="p-inputtext-sm w-full"
      onValueChange={e => handleTimestampChange(body, e)}
    />
  );

  const deleteBtn = (body: IGanttTimestamp) => (
    <Button
      type="button"
      icon="pi pi-trash"
      className="p-button-danger p-button-text p-button-rounded p-button-raised"
      onClick={() =>
        setCurrentConfig(prev => ({
          ...prev,
          ganttTimestamps: prev.ganttTimestamps.toSpliced(
            prev.ganttTimestamps.findIndex(ob => ob.id === body.id),
            1
          ),
        }))
      }
    />
  );

  const handleCreateNewTimestamp = () => {
    const initialTimestamp: IGanttTimestamp = {
      id: uuidv4(),
      color: getHexRandomColor(),
      dateColumn: null,
      icon: null,
      title: null,
    };
    setCurrentConfig(prev => ({
      ...prev,
      ganttTimestamps: [...prev.ganttTimestamps, initialTimestamp],
    }));
  };

  return (
    <>
      <DataTable value={currentConfig.ganttTimestamps}>
        <Column field="title" header={T("word.title")} className="p-1" body={renderTitleInput} />
        <Column
          field="startDate"
          header={T("word.date-column")}
          className="p-1"
          body={renderDropdown}
        />
        <Column field="endDate" header={T("word.icon")} className="p-1" body={renderIconDropdown} />
        <Column field="color" header={T("word.color")} className="p-1" body={renderColorPicker} />
        <Column body={deleteBtn} />
      </DataTable>
      <div className="flex mt-3">
        <Button
          type="button"
          icon="pi pi-plus"
          className="p-button-outlined p-button-rounded p-button-success mt-3 ml-3"
          title={T("action.add")}
          onClick={handleCreateNewTimestamp}
        />
      </div>
    </>
  );
};

export default GanttTimestamps;
